import { Box, Group, Stack } from "@mantine/core"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { ButtonDetails } from "@/components/controls"
import { SessionLink, UserSessionLink } from "@/components/sessions"
import { useModalUserSessionDetails } from "@/components/surveys/details/ModalUserSessionDetails"
import { AdminOnly, AppPagination, AppTitle, DateTime, icons, Score } from "@/components/utils"
import { useAPIResources, useAppPagination } from "@/hooks"
import type { TUser, TUserSession } from "@/types"
import { surveyStatsUserSessionsAPI, usersessionAPI } from "@/utils"
import { BlockSkeleton, Panel } from "./utils"

export function ProfileSessions({ user }: { user: TUser }) {
  const { t } = useTranslation()
  const { data: { data = [] } = {}, isPending } = useAPIResources(usersessionAPI, {
    sort: ["-session__dtstart"],
    where: {
      is_anonymous: false,
      user_id: user.id,
      state: "finished",
      session__type: { $in: ["survey", "pulse", "onboarding"] },
    },
  })

  // Pagination using custom hook
  const pagination = useAppPagination(5)
  const total = data?.length || 0
  useEffect(() => {
    pagination.setTotal(total)
  }, [total])

  const pageData = data?.slice(pagination.offset, pagination.offset + pagination.limit) || []

  const { data: { data: scores = [] } = {} } = useAPIResources(surveyStatsUserSessionsAPI, {
    enabled: !!pageData?.length,
    where: { id: { $in: data?.map((usersession) => usersession.id) } },
  })
  if (!isPending && !pageData?.length) return null

  return (
    <Panel
      block={!isPending}
      sm={{
        gridColumnEnd: "span 2",
      }}
      xl={{
        order: 1,
        gridColumnStart: 0,
      }}
    >
      {isPending ? (
        <BlockSkeleton />
      ) : (
        <Stack gap="sm">
          <AppTitle>
            <icons.survey />
            {t("Surveys")}
          </AppTitle>
          {pageData?.map((usersession) => (
            <UserSessionRow
              key={usersession.id}
              usersession={usersession}
              score={scores?.find((score) => score.usersession_id == usersession.id)}
            />
          ))}
          <AppPagination {...pagination} />
        </Stack>
      )}
    </Panel>
  )
}

function UserSessionRow({ score, usersession }: { usersession: TUserSession; score?: { score: number | null } }) {
  const openDetails = useModalUserSessionDetails(usersession)

  return (
    <Group gap="xs" wrap="nowrap">
      <AdminOnly
        fallback={() => <UserSessionLink usersession={usersession}>{usersession.session.title}</UserSessionLink>}
      >
        <SessionLink session={usersession.session}>{usersession.session.title}</SessionLink>
      </AdminOnly>
      <Box ml="auto" />
      {score?.score && <Score text value={score.score} size="md" />}
      <DateTime timestamp={usersession.session.dtstart} format="L" />
      <ButtonDetails onClick={openDetails} flex="0 0 auto" />
    </Group>
  )
}
